/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebFont from 'webfontloader';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';

import { defaultUserCustomTexts, shouldRender, toHex } from '../../../shared/form-lib';
import PreviewFormField from '../../form_fields/components/preview-renderfield';
import { selectFormCalculations, selectFormRules } from '../../form/selectors';
import { FormUtils, validatePresentPage } from '../../form/utils';
import { formDesignProps } from '../../form_fields/ctrl_proptype';
import { FormContext } from '../../shared/contexts/form-context';
import { collectAnalytics } from '../../form/slice-actions/form';
import Timer from '../../form_fields/components/timer/timer';
import { isTriggerField } from '../../../shared/logic_utils';
import ErrorBoundary from '../../errors/components';
import ThirdPartyShare from '../third-party-share';
import PaymentModal from './form-payment-modal';
import ESignPreviewModal from './e-sign/modal';
import FormWatermark from './form-watermark';
import Pagination from './form-pagination';
import FormHeader from './form-header';
import FormFooter from './form-footer';
import {
  FormPageBackgroundWrapper,
  FormControlsFlexWrapper,
  FormControlsWrapper,
  FormPageBackground,
  ReshowIntroPage,
  FormControls,
  Container,
} from './form-preview.styled';
import {
  doPageApplyCalculations,
  doReshowIntroPage,
  doPageApplyLogic,
  updateFieldError,
} from '../../form/reducer/form';
import {
  formPageBackgroundStyles,
  isCalcOperandField,
  isHiddenField,
  formStyles,
  replaceAt,
} from '../../../shared/utils';

const getFormLayout = (formDesign) => {
  if (formDesign.design.form_layout === 'side_layout right_layout') return 'flex-end';
  if (formDesign.design.form_layout === 'side_layout left_layout') return 'flex-start';
  return 'center';
};

const NewFormPreview = ({ form, rowCtrls, formDesign }) => {
  const {
    eSign,
    form_id,
    payments,
    hiddenFields,
    editingResponse,
    hasIntroMessage,
    reshowIntroPage,
    userCustomTexts,
    triggeringFields,
    openPaymentModal,
    phoneNumberFields,
  } = useSelector((state) => state.form);
  const { PLAN, currentPageNo, inForm, inCustomise, setCurrentPageNo } = useContext(FormContext);
  const formCalculations = useSelector((state) => inForm && selectFormCalculations(state));
  const formRules = useSelector((state) => inForm && selectFormRules(state));
  const newCss = replaceAt(formDesign.old.form_external_css, 0, '');
  const formUtils = useMemo(() => new FormUtils(), []);
  const { onBlurForm, onFocusForm } = formUtils;
  const formBodyRef = useRef(null);
  const dispatch = useDispatch();

  const [timeUp, setTimeUp] = useState({
    quizEnd: false,
    eventEnd: false,
  });
  const [percent, setPercent] = useState(0);
  const [progress, setProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [isSubmissionTriggered, triggerFormSubmission] = useState(false);

  const {
    options,
    form_options,
    definition: { pages },
  } = form;

  const {
    enable_timer: enableTimer,
    timer_options: timerOptions,
    social_options: socialOptions,
    enable_esignature: enableESignature,
  } = inForm ? form_options : options;

  const visiblePages = pages.filter((p) => !hiddenFields[p.internal_id]);
  const formPages = inForm ? visiblePages : pages;
  const presentPage = formPages[currentPageNo - 1];
  const noOfPages = formPages.length;
  const {
    timer,
    timer_display,
    timer_purpose,
    timer_position,
    timer_finish_do,
    timer_description,
    timer_countdown_to,
  } = timerOptions;

  const isEditedByUser = window.location.search?.includes('user');
  const isPreviewingFormOnOtherPages = window.location.search?.includes('FPLS_PREVIEWING');

  const formBodyStyles = {
    marginTop: 25,
    marginBottom: 25,
    padding: `${formDesign.design.form_body_padding / 2}px ${formDesign.design.form_body_padding * 1.1}px`,
  };

  const scrollToTop = () => {
    const props = {
      top: 0,
      left: 0,
      behavior: 'smooth',
    };
    if (inForm) return window.scroll(props);
    return formBodyRef.current.scrollTo(props);
  };

  useEffect(() => {
    if (formDesign.old.form_font) {
      WebFont.load({
        google: {
          families: [`${formDesign.old.form_font}:300,400,500,600,700`],
        },
      });
    }
  }, [formDesign.old.form_font]);

  useEffect(() => {
    scrollToTop();
    if (inForm) dispatch(collectAnalytics());
  }, []);

  useEffect(() => {
    if (inForm) {
      window.addEventListener('blur', onBlurForm);
      window.addEventListener('focus', onFocusForm);
    }
    return () => {
      window.removeEventListener('blur', onBlurForm);
      window.removeEventListener('focus', onFocusForm);
    };
  }, [inForm]);

  useEffect(() => {
    setPercent(Math.round((currentPageNo / noOfPages) * 100));
  }, [currentPageNo, noOfPages]);

  useEffect(() => {
    if (!presentPage) {
      setCurrentPageNo((prev) => (prev !== 0 ? prev - 1 : prev));
    }
  }, [presentPage, setCurrentPageNo]);

  const changePage = async (delta) => {
    function enableChangePage() {
      if (!progress) setProgress(true);
      if (delta > 0 && currentPageNo === noOfPages) return;
      setCurrentPageNo(currentPageNo + delta);
    }

    if (inForm && !isPreviewingFormOnOtherPages) {
      const pageIndex = currentPageNo - 1;

      const validationObj = {
        presentPage,
        hiddenFields,
        setErrorMessage,
        formId: form_id,
        userCustomTexts,
        phoneNumberFields,
      };
      const { atLeastOneWrong, ctrls } = await validatePresentPage(validationObj);

      // Update field errors and scroll to first invalid field
      if (ctrls.length && delta > 0) {
        const errorCtrls = [];
        ctrls.forEach((ct) => {
          if (ct?._validationError) {
            if (Array.isArray(ct._validationError)) {
              ct._validationError.forEach((arr) => {
                if (arr?.some((erx) => erx !== '')) errorCtrls.push(ct);
              });
            } else {
              errorCtrls.push(ct);
            }
          }
        });

        if (errorCtrls.length) {
          document.getElementById(`fpl-${errorCtrls[0].internal_id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }

        dispatch(updateFieldError({ ctrls, pageIndex }));
      }

      // Always allow switch to previous page
      if (!atLeastOneWrong || (atLeastOneWrong && delta < 0)) {
        enableChangePage();
        scrollToTop();
        setErrorMessage('');
      }
    } else {
      enableChangePage();
      scrollToTop();
    }
  };

  const isTimeElapsed = () => {
    setTimeUp({
      quizEnd: timer_purpose === 'quiz',
      eventEnd: timer_purpose === 'event',
    });
  };

  useEffect(() => {
    if (!presentPage) return;

    const { controls } = presentPage;

    if (formRules.length) {
      const triggerableControls = controls.filter((ctrl) => isTriggerField(triggeringFields, ctrl.internal_id));
      if (triggerableControls.length) {
        dispatch(doPageApplyLogic({ triggerableControls }));
      }
    }

    if (formCalculations.length) {
      const calculationOperands = controls.filter((ctrl) => isCalcOperandField(formCalculations, ctrl));
      if (calculationOperands.length) {
        dispatch(doPageApplyCalculations({ calculationOperands }));
      }
    }
  }, [formRules.length, formCalculations.length, JSON.stringify(presentPage)]);

  const ctrls = cloneDeep(formPages).flatMap((page) => {
    const isNotHiddenPages = !isHiddenField(page, hiddenFields);
    return isNotHiddenPages && page.controls;
  });

  // To be modified for used in the new payment flow
  // DON'T DELETE PLEASE: const paymentFieldTypes = ['paystack', 'paypal', 'stripe', 'flutterwave'];
  // DON'T DELETE PLEASE: ctrls.filter((ctrl) => paymentFieldTypes.includes(ctrl.type));
  const paymentCtrls = ctrls.filter((ctrl) => ctrl.props.is_inventory_payment);

  const isPaymentCompleted = paymentCtrls.every((control) => {
    const {
      type,
      value,
      internal_id,
      props: { required },
    } = control;
    const payment = payments[type][internal_id];
    return (!required && !payment?.isPaid) || (editingResponse && value) || payment?.isPaid;
  });

  const shouldRequestEsignAgreement = Boolean(
    enableESignature &&
      !eSign.auditTrail.signed &&
      ctrls.some(({ value, type, props: { required } }) => type === 'signature' && (required || value))
  );

  return (
    <FormPageBackgroundWrapper customCss={newCss} id={reshowIntroPage ? 'hide' : undefined}>
      <FormPageBackground
        inForm={inForm}
        inCustomise={inCustomise}
        formSubmitted={formSubmitted}
        className="form-page-background"
        layoutType={getFormLayout(formDesign)}
        style={{
          '--form-dir': getFormLayout(formDesign),
        }}
        formDirection={formDesign.design.form_direction}
        pageBackground={formPageBackgroundStyles(formDesign)}
        // customBackground={toHex(formDesign.old.page_background)}
      >
        <Container
          inForm={inForm}
          ref={formBodyRef}
          enableTimer={enableTimer}
          layoutType={getFormLayout(formDesign)}
          formPageStyles={formStyles(formDesign)}
          style={{
            '--form-width': `${formDesign.design.custom_form_width}%`,
            '--form-border-radius': `${formDesign.design.form_border_radius}px`,
          }}>
          <FormHeader formName={form.name} formDesign={formDesign} />

          <FormControlsWrapper style={formBodyStyles}>
            {inForm && hasIntroMessage && (
              <ReshowIntroPage
                onClick={() => dispatch(doReshowIntroPage())}
                style={{
                  '--font-family': formDesign.old.form_font,
                  '--font-size': formDesign.old.form_font_size,
                  '--form-text-color': toHex(formDesign.old.form_text_color),
                  '--form-body-label-color': formDesign.design.form_body_label_color,
                }}>
                {userCustomTexts.view_intro_page || defaultUserCustomTexts.view_intro_page}
              </ReshowIntroPage>
            )}

            <FormControlsFlexWrapper enableTimer={enableTimer} timerPosition={timer_position}>
              {!isEditedByUser && enableTimer && !formSubmitted && !submitError && (
                <Timer
                  timer={timer}
                  formDesign={formDesign}
                  timerDisplay={timer_display}
                  timerPurpose={timer_purpose}
                  isTimeElapsed={isTimeElapsed}
                  timerDescription={timer_description}
                  timerCountdownTo={timer_countdown_to}
                  pauseTime={submitInProgress || formSubmitted || !!submitError}
                />
              )}

              {openPaymentModal && (
                <PaymentModal
                  onPaymentCompleted={triggerFormSubmission}
                  isPaymentCompleted={isPaymentCompleted}
                  hiddenFields={hiddenFields}
                  formDesign={formDesign}
                  ctrls={paymentCtrls}
                  plan={PLAN}
                />
              )}

              {eSign.openModal && <ESignPreviewModal onCompleted={triggerFormSubmission} form={form} />}

              <FormControls>
                {formPages.map(
                  (page) =>
                    !isHiddenField(page, hiddenFields) &&
                    page.internal_id === presentPage?.internal_id && (
                      <div key={`page-${page.internal_id}`} className="page">
                        {page.controls.map((ctrl) => {
                          if (shouldRender(ctrl, PLAN, hiddenFields)) {
                            return (
                              <React.Fragment key={`ctrl-${ctrl.internal_id}`}>
                                <ErrorBoundary
                                  title={`Customise Form Field (${ctrl.internal_id}): ${ctrl.type
                                    .split('_')
                                    .join(' ')}`}>
                                  <PreviewFormField ctrl={ctrl} rowCtrls={rowCtrls} formDesign={formDesign} />
                                </ErrorBoundary>
                              </React.Fragment>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )
                )}
              </FormControls>
            </FormControlsFlexWrapper>
          </FormControlsWrapper>

          {presentPage && (
            <Pagination
              form={form}
              timeUp={timeUp}
              percent={percent}
              progress={progress}
              noOfPages={noOfPages}
              formDesign={formDesign}
              changePage={changePage}
              visiblePages={formPages}
              enableTimer={enableTimer}
              presentPage={presentPage}
              submitError={submitError}
              errorMessage={errorMessage}
              timerPurpose={timer_purpose}
              formSubmitted={formSubmitted}
              currentPageNo={currentPageNo}
              timerFinishDo={timer_finish_do}
              formBodyStyles={formBodyStyles}
              setSubmitError={setSubmitError}
              setErrorMessage={setErrorMessage}
              setFormSubmitted={setFormSubmitted}
              submitInProgress={submitInProgress}
              isPaymentCompleted={isPaymentCompleted}
              setSubmitInProgress={setSubmitInProgress}
              isSubmissionTriggered={isSubmissionTriggered}
              shouldRequestEsignAgreement={shouldRequestEsignAgreement}
            />
          )}

          <FormFooter formDesign={formDesign} />
          <FormWatermark useInvisibleRecaptcha={form.use_invisible_captcha} formDesign={formDesign} />
          {!inCustomise && <ThirdPartyShare socialOptions={socialOptions} formName={form.name} />}
        </Container>
      </FormPageBackground>
    </FormPageBackgroundWrapper>
  );
};

NewFormPreview.propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  rowCtrls: PropTypes.objectOf(PropTypes.any).isRequired,
  formDesign: PropTypes.shape(formDesignProps).isRequired,
};

export default NewFormPreview;
